import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, can, ROLE_LABELS } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  House, Users, CurrencyEur, SoccerBall, QrCode, ChartBar, SignOut,
  UserCircle, ListNumbers, ArrowUp, ArrowDown, Wallet, CalendarBlank, PaintBrush, CalendarStar, ChartLineUp, ShieldWarning, Tag
} from "@phosphor-icons/react";
import AppLogo from "@/components/AppLogo";
import { logger } from "@/lib/logger";

const NAV_SECTIONS = [
  {
    title: "Gestión",
    items: [
      { to: "/", label: "Información General", icon: House, roles: ["admin", "taquilla", "tesoreria", "directiva"] },
      { to: "/socios", label: "Socios", icon: Users, roles: ["admin", "taquilla", "tesoreria", "directiva"] },
      { to: "/cuotas", label: "Cuotas y pagos", icon: CurrencyEur, roles: ["admin", "taquilla", "tesoreria", "directiva"] },
      { to: "/partidos", label: "Partidos", icon: SoccerBall, roles: ["admin", "directiva"] },
      { to: "/incidencias", label: "Incidencias", icon: PaintBrush, roles: ["admin", "taquilla", "tesoreria", "directiva"] },
      { to: "/escaner", label: "Control de accesos", icon: QrCode, roles: ["portero"] },
      { to: "/portero", label: "Entradas manuales", icon: ListNumbers, roles: ["portero"] },
      { to: "/aforo", label: "Aforo en directo", icon: ChartBar, roles: ["admin", "portero", "directiva"] },
    ],
  },
  {
    title: "Economía",
    items: [
      { to: "/ingresos", label: "Ingresos", icon: ArrowUp, roles: ["admin", "taquilla", "tesoreria", "directiva"] },
      { to: "/gastos", label: "Gastos", icon: ArrowDown, roles: ["admin", "tesoreria", "directiva"] },
      { to: "/caja", label: "Caja del partido", icon: Wallet, roles: ["admin", "tesoreria", "directiva"] },
      { to: "/balance", label: "Balance anual", icon: ChartLineUp, roles: ["admin", "tesoreria", "directiva"] },
    ],
  },
  {
    title: "Configuración",
    items: [
      { to: "/temporadas", label: "Temporadas", icon: CalendarStar, roles: ["admin"] },
      { to: "/tipos-socio", label: "Tipos de socio", icon: Tag, roles: ["admin"] },
      { to: "/diseno", label: "Diseño del club", icon: PaintBrush, roles: ["admin"] },
      { to: "/usuarios", label: "Usuarios", icon: UserCircle, roles: ["admin"] },
    ],
  },
];

// Mobile tabs use shorter labels to fit better
const MOBILE_LABEL = {
  "/": "Info",
  "/socios": "Socios",
  "/cuotas": "Cuotas",
  "/partidos": "Partidos",
  "/escaner": "Accesos",
  "/portero": "Entradas",
  "/aforo": "Aforo",
  "/incidencias": "Incidencias",
  "/ingresos": "Ingresos",
  "/gastos": "Gastos",
  "/caja": "Caja",
  "/balance": "Balance",
  "/usuarios": "Usuarios",
  "/temporadas": "Temporadas",
  "/tipos-socio": "Tipos de socio",
  "/diseno": "Diseño",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [club, setClub] = useState({ club_name: "Entradas Deportivas", logo_url: null });
  const [now, setNow] = useState(new Date());
  const [backupStatus, setBackupStatus] = useState(null); // { last_backup_at, days_since }
  const [backupDismissed, setBackupDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/matches/active").catch(() => ({ data: null })),
          api.get("/club-config").catch(() => ({ data: {} })),
        ]);
        setMatch(m.data);
        if (c.data) setClub({ club_name: c.data.club_name || "Entradas Deportivas", logo_url: c.data.logo_url || null });
      } catch (e) { logger.debug("Layout header load:", e?.message); }
    })();
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Recordatorio de backup: sólo admin, sólo si nunca se ha hecho o hace > 90 días
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const { data } = await api.get("/seasons/backup-status");
        setBackupStatus(data);
      } catch (e) { /* silencioso */ }
    })();
  }, [user]);

  const showBackupWarning = user?.role === "admin" && !backupDismissed && backupStatus && (
    backupStatus.days_since === null || backupStatus.days_since === undefined || backupStatus.days_since >= 90
  );

  // Filtro estricto: el usuario solo ve el item si su rol está literalmente en la lista
  const hasNav = (item) => !!user && item.roles.includes(user.role);

  // Flat list of items the user can access (for mobile tabs)
  const allItems = NAV_SECTIONS.flatMap((s) => s.items).filter(hasNav);

  return (
    <div className="min-h-screen flex bg-[#F5F7FA] text-slate-900">
      {/* Sidebar (desktop only) */}
      <aside className="hidden lg:flex z-50 w-64 shrink-0 sidebar-bg text-white flex-col h-screen sticky top-0">
        <div className="px-5 pt-6 pb-5 flex items-center gap-3 border-b border-white/10" data-testid="sidebar-app-header">
          <div className="h-11 w-11 flex items-center justify-center shrink-0">
            <AppLogo size={44} rounded={false} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-bold text-base tracking-tight truncate">Entradas Deportivas</div>
            <div className="text-[11px] text-white/55">Sistema de Gestión</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
          {NAV_SECTIONS.map((section) => {
            const items = section.items.filter(hasNav);
            if (items.length === 0) return null;
            return (
              <div key={section.title}>
                <div className="px-3 mb-2 text-[10px] tracking-[0.18em] font-semibold uppercase text-white/45">
                  {section.title}
                </div>
                <div className="space-y-0.5">
                  {items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.to === "/"}
                      data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                          isActive
                            ? "bg-[#2563EB] text-white shadow-md shadow-blue-900/40"
                            : "text-white/75 hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      <n.icon size={18} weight="regular" />
                      <span className="font-medium">{n.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          {club.logo_url && (
            <div
              data-testid="sidebar-club-badge"
              className="flex items-center gap-3 px-3 py-2 bg-white/5 rounded-lg"
            >
              <img
                src={club.logo_url}
                alt={club.club_name}
                className="h-10 w-10 rounded-full object-cover bg-white shadow-lg shrink-0"
              />
              <div className="leading-tight min-w-0">
                <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 font-bold">Club</div>
                <div className="text-xs font-bold text-white truncate">{club.club_name}</div>
              </div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/75 hover:bg-white/5 hover:text-white transition text-sm font-medium"
          >
            <SignOut size={18} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          {/* Top row */}
          <div className="px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center gap-3">
            {/* Mobile brand */}
            <div className="lg:hidden flex items-center gap-2.5 min-w-0">
              <div className="h-9 w-9 flex items-center justify-center shrink-0">
                <AppLogo size={36} rounded={false} />
              </div>
              <div className="leading-tight min-w-0">
                <div className="font-bold text-sm tracking-tight truncate">Entradas Deportivas</div>
                {match && (
                  <div className="text-[10px] text-slate-500 truncate">vs {match.rival}</div>
                )}
              </div>
            </div>

            <div className="flex-1" />

            {/* Active match info (desktop) */}
            {match && (
              <div className="hidden md:block px-6 border-l border-r border-slate-200 py-2">
                <div className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">Partido Activo</div>
                <div className="font-bold text-slate-900 text-sm" data-testid="topbar-match-name">
                  {club.club_name} vs {match.rival}
                </div>
                <div className="text-[11px] text-slate-500">{match.competicion}</div>
              </div>
            )}

            {/* Date / Time */}
            <div className="hidden sm:flex flex-col items-end text-xs text-slate-600">
              <div className="flex items-center gap-1.5">
                <CalendarBlank size={13} />
                <span className="font-medium">
                  {now.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </span>
              </div>
              <div className="font-medium tabular-nums mt-0.5">
                {now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>

            {/* User */}
            <div className="flex items-center gap-2 sm:gap-3 sm:pl-3 sm:border-l border-slate-200">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                <UserCircle size={22} className="sm:hidden" />
                <UserCircle size={26} className="hidden sm:block" />
              </div>
              <div className="hidden sm:block text-right leading-tight">
                <div className="font-bold text-sm text-slate-900" data-testid="header-user-name">{user?.name}</div>
                <div className="text-xs text-slate-500">{ROLE_LABELS[user?.role]}</div>
              </div>
              {/* Mobile logout */}
              <button
                data-testid="mobile-logout-btn"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition"
                title="Cerrar sesión"
              >
                <SignOut size={18} />
              </button>
            </div>
          </div>

          {/* Recordatorio de backup para admins */}
          {showBackupWarning && (
            <div
              data-testid="backup-warning-banner"
              className="bg-amber-50 border-t border-amber-200 px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ShieldWarning size={18} weight="fill" className="text-amber-600 shrink-0" />
                <div className="text-xs sm:text-sm text-amber-900 min-w-0">
                  <strong className="font-bold">Recordatorio de copia de seguridad:</strong>{" "}
                  {backupStatus.days_since === null || backupStatus.days_since === undefined
                    ? "Aún no se ha descargado ninguna copia de seguridad."
                    : `Han pasado ${backupStatus.days_since} días desde tu último backup${backupStatus.season_name ? ` (${backupStatus.season_name})` : ""}.`}
                  {" "}Descarga una copia antes de purgar cualquier temporada.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid="backup-warning-go-btn"
                  onClick={() => navigate("/temporadas")}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-3 h-8 text-[11px] font-display font-bold uppercase tracking-widest transition"
                  style={{ borderRadius: 6 }}
                >
                  Ir a Temporadas
                </button>
                <button
                  data-testid="backup-warning-dismiss-btn"
                  onClick={() => setBackupDismissed(true)}
                  className="text-amber-700 hover:text-amber-900 text-xs font-bold px-2 h-8"
                  title="Descartar por ahora"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Mobile tab bar (horizontal scroll) */}
          <nav className="lg:hidden border-t border-slate-100 bg-white">
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 px-3 py-2.5 min-w-min">
                {allItems.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`tab-${n.to.replace("/", "") || "home"}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 h-11 rounded-full whitespace-nowrap transition-all text-sm font-bold ${
                        isActive
                          ? "bg-[#2563EB] text-white shadow-md shadow-blue-500/30"
                          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`
                    }
                  >
                    <n.icon size={16} weight="bold" />
                    <span>{MOBILE_LABEL[n.to] || n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>
        </header>

        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
